import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Config } from '../config/config';
import { AuthContext, InitialAuthContext } from '../context/auth';
import { Replica, Voice } from '../types/types';
import { Button, DataContainer, DataLabel, DataRow, DataRowCell, DataValue, Select, TextInput } from './common';
import { CreateAvatarFromImage, VoiceInput } from './createAvatarFromImage';
import { ConfirmModal } from './modal';

// The fields an admin edits; "new" as id means the avatar doesn't exist yet.
type AvatarForm = {
  id: string
  name: string
  description: string
  image_url: string
  video_url: string
  provider: string
  provider_replica_id: string
  provider_avatar_type: 'avatar' | 'talking_photo'
  tts_voice_id: string
  tts_voice_model_id: string
  shared_with_account: boolean
  shared_with_users: string[]
  owner_id: string
}

const NEW_AVATAR: AvatarForm = {
  id: 'new',
  name: '',
  description: '',
  image_url: '',
  video_url: '',
  provider: 'heygen',
  provider_replica_id: '',
  provider_avatar_type: 'avatar',
  tts_voice_id: '',
  tts_voice_model_id: '',
  shared_with_account: true,
  shared_with_users: [],
  owner_id: '',
}

function toForm(replica: Replica): AvatarForm {
  return {
    id: replica.id,
    name: replica.name,
    description: replica.description,
    image_url: replica.image_url,
    video_url: replica.video_url,
    provider: replica.provider || '',
    provider_replica_id: replica.provider_replica_id || '',
    provider_avatar_type: replica.provider_avatar_type || 'avatar',
    tts_voice_id: replica.tts_voice_id || '',
    tts_voice_model_id: replica.tts_voice_model_id || '',
    shared_with_account: replica.shared_with_account,
    shared_with_users: replica.shared_with_users || [],
    owner_id: replica.owner_id,
  }
}

function AvatarThumbnail({replica}: {replica: Replica}) {
  const style: React.CSSProperties = {width: '64px', height: '64px', objectFit: 'cover', borderRadius: '8px', backgroundColor: '#e5e5ee'}
  if (replica.image_url)
    return <img src={replica.image_url} alt={replica.name} style={style} />
  if (replica.video_url)
    return <video src={replica.video_url + '#t=0.5'} muted preload="metadata" style={style} />
  return <div style={style} />
}

function describeAvatar(replica: Replica): string {
  if (!replica.has_avatar)
    return 'Voice only'
  const type = replica.provider_avatar_type === 'talking_photo' ? 'Talking photo' : 'Avatar'
  return `${type} (${replica.provider || 'unknown provider'})`
}

function describeSharing(replica: Replica): string {
  if (replica.shared_with_account)
    return 'Whole account'
  const users = replica.shared_with_users?.length || 0
  return users > 0 ? `Owner + ${users} user${users === 1 ? '' : 's'}` : 'Owner only'
}

// AccountAvatars lists every avatar on an account, whoever owns it, and
// lets a superadmin add, edit and remove them.
export function AccountAvatars({accountId}: {accountId: string}) {
  const {authenticated, setAuthenticated} = useContext(AuthContext)
  const [avatars, setAvatars] = useState<Replica[]>([])
  const [voices, setVoices] = useState<Voice[]>([])
  const [editing, setEditing] = useState<AvatarForm | undefined>()
  const [creatingFromImage, setCreatingFromImage] = useState(false)

  const adminUrl = `${Config.base_url}/api/v1/admin/accounts/${accountId}/replicas`

  const request = useCallback(async (url: string, init: RequestInit = {}) => {
    const res = await fetch(url, {
      ...init,
      headers: {Authorization: "Bearer " + authenticated.token, ...init.headers},
    })
    if (res.status === 401)
      setAuthenticated(InitialAuthContext.authenticated)
    return res
  }, [authenticated.token, setAuthenticated])

  const loadAvatars = useCallback(async () => {
    const res = await request(adminUrl)
    if (!res.ok)
      return
    const resJson = await res.json()
    if (resJson.ok)
      setAvatars(resJson.replicas as Replica[])
  }, [request, adminUrl])

  useEffect(() => {
    loadAvatars()
  }, [loadAvatars])

  useEffect(() => {
    // The TTS key may not be allowed to list voices; voice ids are then
    // entered by hand.
    request(`${Config.base_url}/api/v1/admin/voices`)
      .then(res => res.ok ? res.json() : Promise.reject(res.statusText))
      .then(data => setVoices((data.voices || []) as Voice[]))
      .catch(err => console.error("Failed to fetch voices:", err))
  }, [request])

  const voiceName = (voiceId?: string) => voices.find(voice => voice.voice_id === voiceId)?.name || voiceId || ''

  const save = async (form: AvatarForm) => {
    const isNew = form.id === 'new'
    const {id, ...body} = form
    const res = await request(isNew ? adminUrl : `${adminUrl}/${id}`, {
      method: isNew ? 'POST' : 'PUT',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(body),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      alert("Could not save the avatar" + (data.error ? `: ${data.error}` : ""))
      return
    }
    setEditing(undefined)
    loadAvatars()
  }

  const remove = async (replica: Replica) => {
    if (!window.confirm(`Remove avatar '${replica.name || replica.id}'? Videos already made with it are kept.`))
      return
    const res = await request(`${adminUrl}/${replica.id}`, {method: 'DELETE'})
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      alert("Could not remove the avatar" + (data.error ? `: ${data.error}` : ""))
      return
    }
    loadAvatars()
  }

  return (
    <DataContainer>
      <i>Changes to Avatars are done instantly!</i>
      {avatars.length > 0 ?
        avatars.map(replica => (
          <DataRow key={replica.id} style={{alignItems: 'center'}}>
            <DataRowCell style={{flexGrow: 0}}><AvatarThumbnail replica={replica} /></DataRowCell>
            <DataRowCell>
              <DataLabel>{replica.name || '(unnamed)'}</DataLabel>
              <DataValue title={replica.id}>{replica.description}</DataValue>
            </DataRowCell>
            <DataRowCell><DataValue title={replica.provider_replica_id}>{describeAvatar(replica)}</DataValue></DataRowCell>
            <DataRowCell><DataValue title={replica.tts_voice_id}>{replica.has_voice ? 'Voice: ' + voiceName(replica.tts_voice_id) : 'Provider voice'}</DataValue></DataRowCell>
            <DataRowCell><DataValue title={'Owner ' + replica.owner_id}>{describeSharing(replica)}</DataValue></DataRowCell>
            <DataRowCell style={{flexGrow: 0}}><Button $primary={true} onClick={() => setEditing(toForm(replica))}>Edit</Button></DataRowCell>
            <DataRowCell style={{flexGrow: 0}}><Button $danger={true} onClick={() => remove(replica)}>Remove</Button></DataRowCell>
          </DataRow>
        ))
        :
        <DataRow><DataRowCell><DataValue>No avatars on this account...</DataValue></DataRowCell></DataRow>
      }
      {creatingFromImage ?
        <CreateAvatarFromImage
          endpoint={`${adminUrl}/photo`}
          voices={voices}
          defaultShared={true}
          showSharing={true}
          onCancel={() => setCreatingFromImage(false)}
          onCreated={() => {
            setCreatingFromImage(false)
            loadAvatars()
          }}
        />
        :
        <DataRow>
          <DataRowCell><DataLabel>New</DataLabel></DataRowCell>
          <DataRowCell style={{flexGrow: 0}}><Button $primary={true} onClick={() => setCreatingFromImage(true)}>Create from image</Button></DataRowCell>
          <DataRowCell style={{flexGrow: 0}}><Button $primary={true} onClick={() => setEditing({...NEW_AVATAR})}>Link existing avatar</Button></DataRowCell>
        </DataRow>
      }
      {editing ? <ConfirmModal
        width="600px"
        isOpen={!!editing}
        header={editing.id === 'new' ? 'Link an existing avatar' : `You're editing the avatar '${editing.name}'`}
        cancelText='Cancel'
        submitText={editing.id === 'new' ? 'Create avatar' : 'Save changes'}
        onCancel={() => setEditing(undefined)}
        onConfirm={() => save(editing)}
        text={<AvatarEditor form={editing} voices={voices} onChange={setEditing} />}
      /> : null}
    </DataContainer>
  )
}

function AvatarEditor({form, voices, onChange}: {form: AvatarForm, voices: Voice[], onChange: (form: AvatarForm) => void}) {
  const field = (label: string, input: React.ReactNode) => (
    <DataRow>
      <DataRowCell style={{flexBasis: '35%', flexGrow: 0}}><DataLabel>{label}</DataLabel></DataRowCell>
      <DataRowCell>{input}</DataRowCell>
    </DataRow>
  )
  const text = (key: keyof AvatarForm, placeholder = '') => (
    <TextInput style={{margin: 0, width: '100%'}} placeholder={placeholder} value={form[key] as string}
      onChange={(evt) => onChange({...form, [key]: evt.target.value})} />
  )

  return (
    <DataContainer>
      {field('Name', text('name'))}
      {field('Description', text('description'))}
      {field('Preview image URL', text('image_url', 'https://...'))}
      {field('Preview video URL', text('video_url', 'https://...'))}
      {field('Provider', <Select value={form.provider} onChange={(evt) => onChange({...form, provider: evt.target.value})}>
        <option value="">None (voice only)</option>
        <option value="heygen">HeyGen</option>
        <option value="tavus">Tavus</option>
        {/* Older rows may hold other values (e.g. "t", treated as Tavus); keep them visible. */}
        {form.provider && !['heygen', 'tavus'].includes(form.provider) ? <option value={form.provider}>{form.provider} (treated as Tavus)</option> : null}
      </Select>)}
      {field('Avatar type', <Select value={form.provider_avatar_type} onChange={(evt) => onChange({...form, provider_avatar_type: evt.target.value as AvatarForm['provider_avatar_type']})}>
        <option value="avatar">Avatar (video of a person)</option>
        <option value="talking_photo" disabled={form.provider !== 'heygen'}>Talking photo (HeyGen only)</option>
      </Select>)}
      {field(form.provider_avatar_type === 'talking_photo' ? 'Talking photo ID' : 'Provider avatar ID', text('provider_replica_id'))}
      {field('Voice', <VoiceInput voices={voices} value={form.tts_voice_id} onChange={(voiceId) => onChange({...form, tts_voice_id: voiceId})} />)}
      {field('Voice model', text('tts_voice_model_id', 'Default'))}
      {field('Sharing', <label>
        <input type="checkbox" checked={form.shared_with_account} onChange={(evt) => onChange({...form, shared_with_account: evt.target.checked})} /> Shared with whole account
      </label>)}
      {form.id !== 'new' ? field('Owner (QVP user ID)', text('owner_id')) : null}
      <i>A talking photo needs a voice. Without a voice, HeyGen and Tavus avatars use their own.</i>
    </DataContainer>
  )
}
